import React, { useState } from 'react';
import { BellRing, Image, Link as LinkIcon, Loader2, Megaphone, Send, Sparkles } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const MARKETING_BUCKET = 'marketing-push';
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const initialForm = {
  heading: '',
  content: '',
  imageUrl: '',
  url: '/home',
};

const MarketingPush = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const uploadCampaignImage = async (file) => {
    setError('');
    setResult(null);

    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('La imagen debe ser JPG, PNG o WEBP.');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setError('La imagen no puede pesar más de 3 MB.');
      return;
    }

    setUploadingImage(true);

    try {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'png';
      const fileName = `${Date.now()}-${crypto.randomUUID()}.${extension}`;
      const filePath = `campaigns/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(MARKETING_BUCKET)
        .upload(filePath, file, {
          cacheControl: '31536000',
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from(MARKETING_BUCKET)
        .getPublicUrl(filePath);

      updateField('imageUrl', data.publicUrl);
    } catch (err) {
      console.error('Error subiendo imagen de campaña:', err);
      setError(err?.message || 'No se pudo subir la imagen.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);

    if (!form.heading.trim() || !form.content.trim()) {
      setError('El título y el mensaje son obligatorios.');
      return;
    }

    setSending(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('smooth-function', {
        body: {
          type: 'marketing',
          heading: form.heading.trim(),
          content: form.content.trim(),
          image_url: form.imageUrl.trim() || undefined,
          url: form.url.trim() || '/home',
        },
      });

      if (invokeError) {
        throw invokeError;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setResult(data?.result || data);
    } catch (err) {
      console.error('Error enviando campaña push:', err);
      setError(err?.message || 'No se pudo enviar la campaña push.');
    } finally {
      setSending(false);
    }
  };

  const nativeResult = result?.native;
  const oneSignalResult = result?.onesignal;

  return (
    <div className="space-y-8 pb-10">
      <header>
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
          <Megaphone className="h-3.5 w-3.5" />
          Campañas de marketing
        </div>
        <h2 className="text-2xl font-black tracking-tight text-slate-800">
          Enviar Notificación Push
        </h2>
        <p className="mt-1 max-w-2xl text-sm font-medium leading-relaxed text-slate-500">
          Envía promociones, avisos o campañas a los usuarios que activaron notificaciones. Se entrega por Web Push nativo y, cuando exista, también por OneSignal.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <form
          onSubmit={handleSubmit}
          className="xl:col-span-3 rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm"
        >
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                Título
              </label>
              <input
                value={form.heading}
                onChange={(event) => updateField('heading', event.target.value.slice(0, 80))}
                placeholder="Ej: Promoción especial por tiempo limitado"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
              />
              <p className="mt-1 text-right text-[10px] font-bold text-slate-300">
                {form.heading.length}/80
              </p>
            </div>

            <div>
              <label className="mb-2 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                Mensaje
              </label>
              <textarea
                value={form.content}
                onChange={(event) => updateField('content', event.target.value.slice(0, 240))}
                placeholder="Escribe el contenido de la campaña..."
                rows={5}
                className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold leading-relaxed text-slate-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
              />
              <p className="mt-1 text-right text-[10px] font-bold text-slate-300">
                {form.content.length}/240
              </p>
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                <Image className="h-3.5 w-3.5" />
                Imagen grande opcional
              </label>
              <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/50 p-4">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => uploadCampaignImage(event.target.files?.[0])}
                  disabled={uploadingImage || sending}
                  className="block w-full text-sm font-bold text-slate-600 file:mr-4 file:rounded-xl file:border-0 file:bg-blue-600 file:px-4 file:py-2.5 file:text-xs file:font-black file:uppercase file:tracking-widest file:text-white hover:file:bg-blue-700 disabled:opacity-60"
                />
                {uploadingImage && (
                  <p className="mt-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-blue-600">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Subiendo imagen...
                  </p>
                )}
              </div>
              {form.imageUrl && (
                <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-widest text-emerald-600">
                    Imagen lista para la campaña
                  </p>
                  <button
                    type="button"
                    onClick={() => updateField('imageUrl', '')}
                    className="mt-2 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-red-500"
                  >
                    Quitar imagen
                  </button>
                </div>
              )}
              <p className="mt-2 text-xs font-medium text-slate-400">
                Recomendado: imagen horizontal JPG/PNG/WEBP menor a 3 MB. Android puede mostrarla expandida o al desplegar la notificación.
              </p>
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                <LinkIcon className="h-3.5 w-3.5" />
                URL al tocar la notificación
              </label>
              <input
                value={form.url}
                onChange={(event) => updateField('url', event.target.value)}
                placeholder="/home"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
              />
              <p className="mt-2 text-xs font-medium text-slate-400">
                Puedes usar rutas internas como <span className="font-black text-slate-500">/home</span>, <span className="font-black text-slate-500">/profile</span> o <span className="font-black text-slate-500">/history</span>.
              </p>
            </div>

            {error && (
              <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={sending}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black uppercase tracking-widest text-white shadow-xl shadow-blue-100 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando campaña...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Enviar Push
                </>
              )}
            </button>
          </div>
        </form>

        <aside className="xl:col-span-2 space-y-6">
          <div className="rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <BellRing className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Vista previa
                </p>
                <h3 className="text-sm font-black text-slate-800">Notificación</h3>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-slate-950 text-white shadow-2xl shadow-slate-200">
              {form.imageUrl && (
                <img
                  src={form.imageUrl}
                  alt="Vista previa de campaña"
                  className="h-36 w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="p-5">
                <div className="mb-3 flex items-center gap-3">
                  <img src="/notification-icon.png" alt="" className="h-9 w-9" />
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-200">
                      Remesas Venezuela
                    </p>
                    <p className="text-xs font-bold text-slate-400">Ahora</p>
                  </div>
                </div>
                <h4 className="text-base font-black leading-tight">
                  {form.heading || 'Título de la campaña'}
                </h4>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-300">
                  {form.content || 'Aquí se verá el mensaje que recibirán los usuarios.'}
                </p>
              </div>
            </div>
          </div>

          {result && (
            <div className="rounded-[32px] border border-emerald-100 bg-emerald-50 p-6">
              <div className="mb-4 flex items-center gap-2 text-emerald-700">
                <Sparkles className="h-4 w-4" />
                <p className="text-xs font-black uppercase tracking-widest">Campaña enviada</p>
              </div>
              <div className="space-y-3 text-sm font-bold text-emerald-900">
                <p>Nativo: {nativeResult?.sent ?? 0} enviadas, {nativeResult?.failed ?? 0} fallidas.</p>
                <p>OneSignal: {oneSignalResult?.id ? 'enviada' : oneSignalResult?.skipped ? 'omitida' : 'procesada'}.</p>
                {nativeResult?.failures?.length > 0 && (
                  <div className="rounded-2xl bg-white/70 p-3 text-xs text-amber-700">
                    <p className="mb-2 font-black uppercase tracking-widest">Fallos detectados</p>
                    {nativeResult.failures.map((failure) => (
                      <p key={failure.id}>
                        #{failure.id} {failure.provider}: {failure.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default MarketingPush;
